import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FileText, MapPin, DollarSign, Calendar } from "lucide-react";
import { Chip } from "@mui/material";
import type { Application } from "../../../hooks/candidate/useCandidateData";
import { APPLICATION_STATUS_CONFIG } from "../../../components/StatusControl/configs/applicationConfig";
import ApplicationStatusTimeline from "../../../components/ApplicationStatusTimeline";
import QuickStatusChange from "../../../components/QuickStatusChange";
import ApplicationKamAssignment from "../../../components/ApplicationKamAssignment";

interface ApplicationsTabProps {
  applications: Application[];
  applicationsLoading: boolean;
  onCreateApplication: () => void;
  onGenerateJobSpecificCV: (application: Application) => void;
}

const ApplicationsTab: React.FC<ApplicationsTabProps> = ({
  applications,
  applicationsLoading,
  onCreateApplication,
  onGenerateJobSpecificCV,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const navigate = useNavigate();

  return (
    <div className="applications-tab">
      <div className="applications-header">
        <h3>{t("applications.title")}</h3>
        <button onClick={onCreateApplication} className="btn-primary">
          + {t("applications.newApplication")}
        </button>
      </div>

      {applicationsLoading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t("common:loading.applications")}</p>
        </div>
      )}
      {!applicationsLoading && applications.length === 0 && (
        <div className="empty-state">
          <p>{t("applications.noApplicationsForCandidate")}</p>
        </div>
      )}
      {!applicationsLoading && applications.length > 0 && (
        <div className="applications-list">
          {applications.map((application) => {
            const statusConfig = APPLICATION_STATUS_CONFIG[application.status];
            const borderColor = statusConfig?.color || "#6b7280";
            return (
              <div
                key={application.id}
                className="application-item"
                style={{ borderLeftColor: borderColor }}
              >
                <a
                  href={`/applications/${application.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/applications/${application.id}`);
                  }}
                  aria-label={application.job_title}
                  className="application-item-link"
                />

                {/* Row 1: Job title + Status chip */}
                <div className="app-row-header">
                  <h4>{application.job_title}</h4>
                  <Chip
                    label={statusConfig?.label || application.status}
                    size="small"
                    sx={{
                      backgroundColor: statusConfig?.color || "#6b7280",
                      color: "white",
                      fontWeight: 500,
                      fontSize: "11px",
                      flexShrink: 0,
                    }}
                  />
                </div>

                {/* Row 2: Meta — location, rate, date */}
                <div className="app-row-meta">
                  {application.job_location && (
                    <span className="meta-item">
                      <MapPin size={13} />
                      {application.job_location}
                    </span>
                  )}
                  {application.expected_rate != null && (
                    <span className="meta-item rate">
                      <DollarSign size={13} />
                      {application.expected_rate}/day
                    </span>
                  )}
                  <span className="meta-item date">
                    <Calendar size={13} />
                    {t("common:dashboard.applied")}:{" "}
                    {new Date(application.created_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Row 3: Timeline */}
                <div className="app-row-timeline">
                  <ApplicationStatusTimeline
                    currentStatus={application.status}
                    statusChangedAt={application.status_changed_at}
                    compact
                  />
                </div>

                {/* Row 4: KAM Assignment */}
                <div
                  className="app-row-kam"
                  style={{ position: "relative", zIndex: 1 }}
                >
                  <ApplicationKamAssignment applicationId={application.id} />
                </div>

                {/* Row 5: Quick actions + Generate CV */}
                <div
                  className="app-row-actions"
                  style={{ position: "relative", zIndex: 2 }}
                >
                  <div className="app-quick-actions">
                    <QuickStatusChange
                      applicationId={application.id}
                      currentStatus={application.status}
                      compact
                    />
                  </div>
                  <button
                    className="btn-generate-cv"
                    onClick={() => onGenerateJobSpecificCV(application)}
                  >
                    <FileText size={14} /> {t("candidates.detail.generateCV")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApplicationsTab;
